import { FieldErrors, FieldValues } from 'react-hook-form';
import { HiOutlineBriefcase } from 'react-icons/hi';
import { LuMail, LuBuilding, LuGlobe } from 'react-icons/lu';
import {
  AutomatedEmailCampaignType,
  IAutomatedEmailCampaignSetting,
  IAutomatedEmailCampaignSettingListPayload,
} from 'wisestep-api';
import { FieldOption, UserRoundIcon } from 'wisestep-base';

import { IOption } from '../../../components/SettingsIndex';
export const campaignHeaderHelper = {
  emailCheck: {
    header: 'Default Campaign',
    subHeader:
      'Activate the default campaign to automatically email top candidates when a new job is posted.',
  },
  totalEmails: {
    header: 'Total Emails',
    subHeader: 'Specify the number of candidates who will receive this email notification.',
  },
  aiReplyEnabled: {
    header: 'AI Reply Assistant',
    subHeader: 'AI-assisted replies for responding to candidate emails.',
  },
  manualReply: {
    header: 'Manual Reply',
    subHeader: 'Manually assign a user to reply to candidate emails.',
  },
  callBackRequest: {
    header: 'Callback Request',
    subHeader: 'Invite a user to handle callback requests from candidates.',
  },
  clientContactId: {
    header: 'Choose a Client SPOC *',
    subHeader:
      'Activate the campaign to automatically email top candidates when a new job is posted for respective Client SPOC.',
  },
  clientId: {
    header: 'Choose a Client *',
    subHeader:
      'Activate the campaign to automatically email top candidates when a new job is posted for respective Client.',
  },
  jobId: {
    header: 'Choose a Job *',
    subHeader:
      'Activate the campaign to automatically email top candidates when a new job is posted for respective Job.',
  },
  source: {
    header: 'Choose a source  *',
    subHeader:
      'Activate the campaign to automatically email top candidates when a new job is posted for respective Source.',
  },
};

export const campaignTabs = [
  {
    tabName: 'Email Campaign',
  },
  {
    tabName: 'Email Template',
  },
];

export const switchDialogHelper = {
  aiReplyEnabled: {
    header: 'Switching to AI Reply Assistant',
    subHeader: 'Campaigns for new job postings will be replied with AI-assistant.',
    showIcon: true,
    alteredField: 'manualReply',
  },
  manualReply: {
    header: 'Switching to Manual reply',
    subHeader: 'Campaigns for new job postings will require a manual response.',
    showIcon: true,
    alteredField: 'aiReplyEnabled',
  },
  callBackRequest: {
    header: 'Callback Request',
    subHeader: 'Invite a user to handle callback requests from candidates.',
    showIcon: false,
  },
};

export const automatedNavigationOptions: IOption[] = [
  {
    label: 'Default',
    icon: <LuMail />,
    url: '/automated-email-campaign-settings/',
  },
  {
    label: 'Client',
    icon: <LuBuilding />,
    url: '/automated-email-campaign-settings/client',
  },
  {
    label: 'Client SPOC',
    icon: <UserRoundIcon />,
    url: '/automated-email-campaign-settings/client-spoc',
  },
  {
    label: 'Job',
    icon: <HiOutlineBriefcase />,
    url: '/automated-email-campaign-settings/job',
  },
  {
    label: 'Source',
    icon: <LuGlobe />,
    url: '/automated-email-campaign-settings/source',
  },
];

export const defaultAutomatedEmailCampaignSettingListPayload = (
  type: AutomatedEmailCampaignType
): IAutomatedEmailCampaignSettingListPayload => {
  return {
    page: 0,
    size: 20,
    type: type,
    search: '',
  };
};

export const routePath: Record<AutomatedEmailCampaignType, string> = {
  [AutomatedEmailCampaignType.JOB]: '/automated-email-campaign-settings/job',
  [AutomatedEmailCampaignType.SOURCE]: '/automated-email-campaign-settings/source',
  [AutomatedEmailCampaignType.CLIENT]: '/automated-email-campaign-settings/client',
  [AutomatedEmailCampaignType.DEFAULT]: '/automated-email-campaign-settings/',
  [AutomatedEmailCampaignType.CLIENT_SPOC]: '/automated-email-campaign-settings/client-spoc',
};

export const createCampaignEntity = (data: FieldValues, type: AutomatedEmailCampaignType) => {
  const entity = {
    id: data.id,
    totalEmails: data.totalEmails,
    aiReplyEnabled: data.aiReplyEnabled,
    manualReply: data.manualReply,
    manualReplyFrom: data?.manualEmail?.email,
    callBackRequest: data.callBackRequest,
    callBackRequestFrom: data?.callbackEmail?.email,
    emailBody: data.emailBody,
    emailReplyPrompts: data.emailReplyPrompts,
    subject: data.subject,
    emailCheck: false,
    fromEmail: data.fromEmail,
    preferredAccountId: data.preferredAccountId,
  } as IAutomatedEmailCampaignSetting;

  switch (type) {
    case AutomatedEmailCampaignType.CLIENT:
      if (data.client) {
        entity.metaDetails = data.client;
        entity.clientId = data.client.value;
      }
      break;

    case AutomatedEmailCampaignType.CLIENT_SPOC:
      if (data.clientContact) {
        entity.metaDetails = data.clientContact;
        entity.clientContactId = data.clientContact.id;
      }
      break;

    case AutomatedEmailCampaignType.JOB:
      if (data.job) {
        entity.metaDetails = data.job;
        entity.jobId = data.job.id;
      }
      break;

    case AutomatedEmailCampaignType.SOURCE:
      if (data.source) {
        entity.source = data.source;
      }
      break;
  }
  return entity;
};

export const extractAllMessages = (errors: FieldErrors): string[] => {
  const messages: string[] = [];

  const traverse = (obj: FieldErrors | any) => {
    for (const key in obj) {
      const value = obj[key];
      if (!value) continue;
      if (typeof value.message === 'string') {
        messages.push(value.message);
      }
      if (typeof value === 'object') {
        traverse(value);
      }
    }
  };

  traverse(errors);
  return messages;
};

export const getCampaignUniqueId = (
  data: FieldValues,
  campaignType: AutomatedEmailCampaignType
) => {
  let id;
  switch (campaignType) {
    case AutomatedEmailCampaignType.CLIENT:
      id = data.client.value;
      break;

    case AutomatedEmailCampaignType.CLIENT_SPOC:
      id = data.clientContact.id;
      break;
    case AutomatedEmailCampaignType.JOB:
      id = data.job.id;
      break;

    case AutomatedEmailCampaignType.SOURCE:
      id = data.source;
      break;
  }
  return id;
};

export const getCampaignSettingsColumns = (
  campaignType: AutomatedEmailCampaignType
): FieldOption[] => {
  const baseFields: FieldOption[] = [
    { id: 'createdOn', checked: true },
    { id: 'status', checked: true },
    { id: 'id', checked: true },
  ];

  let prefixFields: FieldOption[] = [];

  switch (campaignType) {
    case AutomatedEmailCampaignType.CLIENT:
      prefixFields = [{ id: 'client', checked: true, required: true }];
      break;
    case AutomatedEmailCampaignType.CLIENT_SPOC:
      prefixFields = [
        { id: 'clientSpocName', checked: true, required: true },
        { id: 'client', checked: true, required: true },
      ];
      break;
    case AutomatedEmailCampaignType.JOB:
      prefixFields = [
        { id: 'title', checked: true, required: true },
        { id: 'jobCode', checked: true, required: true },
      ];
      break;
    case AutomatedEmailCampaignType.SOURCE:
      prefixFields = [{ id: 'source', checked: true, required: true }];
      break;
  }

  return [...prefixFields, ...baseFields];
};
